import { resolve, singleton, ISignaler } from 'aurelia';
import { SavedSearch, ToyRelease, isReleaseUpcoming } from '../models';
import { HttpClient } from './http-client';
import { AccountService } from './account-service';

// Signal dispatched whenever subscription state changes. Bell bindings that call helper methods
// (e.g. isReleaseSubscribed) use `& signal` so Aurelia re-evaluates them on demand, since it only
// observes a method binding's arguments, not the Set state read inside the method body.
export const SUBSCRIPTIONS_SIGNAL = 'subscriptions-changed';

// Owns the per-item notification "bell" toggles. Held as sets of target keys for O(1) binding
// lookups: saved-search ids (as strings) and release keys (DedupeKeys). Only meaningful for
// signed-in users. Kept in sync with the server via /api/subscriptions.
@singleton()
export class SubscriptionService {
  private readonly http: HttpClient = resolve(HttpClient);
  private readonly account: AccountService = resolve(AccountService);
  // Dispatches SUBSCRIPTIONS_SIGNAL so `& signal` bell bindings refresh after a toggle.
  private readonly signaler: ISignaler = resolve(ISignaler);

  savedSearchSubscriptions: Set<string> = new Set<string>();
  releaseSubscriptions: Set<string> = new Set<string>();
  // Target keys with a subscribe/unsubscribe request in flight, so buttons can disable/spin.
  private pendingSubscriptions: Set<string> = new Set<string>();

  // Set to a user-facing message when a release subscription is rejected because the per-user
  // reminder cap was reached (HTTP 409 from the API). <subscription-limit-toast> binds to this to
  // tell the user the reminder was not set; it auto-clears after a short delay.
  limitReachedMessage: string | null = null;
  private limitReachedTimer: ReturnType<typeof setTimeout> | null = null;

  // Loads the signed-in user's subscriptions (both kinds) into the lookup sets. Best-effort:
  // failures leave the sets empty so bells simply render as "off".
  async loadSubscriptions(): Promise<void> {
    this.savedSearchSubscriptions = new Set<string>();
    this.releaseSubscriptions = new Set<string>();
    if (!this.account.isAuthenticated) return;
    try {
      const response = await this.http.get('/api/subscriptions');
      if (!response.ok) return;
      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items : [];
      const savedSearch = new Set<string>();
      const release = new Set<string>();
      for (const item of items) {
        if (typeof item?.targetKey !== 'string') continue;
        if (item.kind === 'saved-search') {
          savedSearch.add(item.targetKey);
        } else if (item.kind === 'release') {
          release.add(item.targetKey);
        }
      }
      this.savedSearchSubscriptions = savedSearch;
      this.releaseSubscriptions = release;
      this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
    } catch {
      // Non-fatal: leave the sets empty.
    }
  }

  // True when the given saved search currently has a notification bell enabled.
  isSavedSearchSubscribed(search: SavedSearch): boolean {
    return typeof search.id === 'number' && this.savedSearchSubscriptions.has(String(search.id));
  }

  // True when the given release currently has a notification bell enabled.
  isReleaseSubscribed(release: ToyRelease): boolean {
    return !!release.key && this.releaseSubscriptions.has(release.key);
  }

  // True while a subscribe/unsubscribe request for the given target key is in flight.
  isSubscriptionPending(targetKey: string | undefined): boolean {
    return !!targetKey && this.pendingSubscriptions.has(targetKey);
  }

  // A release is availability-eligible (can show a bell) when it has a concrete availability
  // instant that is still in the future — i.e. an announced drop whose availability moment has
  // not yet elapsed. Shares isReleaseUpcoming with the timestamp display so the bell and the
  // time appear together.
  isAvailabilityEligible(release: ToyRelease): boolean {
    return !!release.key && isReleaseUpcoming(release);
  }

  // Toggles the notification bell on a saved search, persisting via /api/subscriptions.
  async toggleSavedSearchSubscription(search: SavedSearch): Promise<void> {
    if (typeof search.id !== 'number') return;
    await this.toggleSubscription('saved-search', String(search.id));
  }

  // Applies a desired subscription state for a saved search: subscribe when true, unsubscribe
  // when false. Used by the "Receive email notifications" checkbox on save so the checkbox is
  // authoritative. A no-op when the search has no server id (anonymous visitors can't subscribe)
  // or when it is already in the desired state.
  async setSavedSearchSubscription(search: SavedSearch, subscribe: boolean): Promise<void> {
    if (typeof search.id !== 'number') return;
    await this.setSubscription('saved-search', String(search.id), subscribe);
  }

  // Toggles the notification bell on a release, persisting via /api/subscriptions. Declared as an
  // arrow property so it keeps `this` bound when passed to the <release> custom element.
  toggleReleaseSubscription = async (release: ToyRelease): Promise<void> => {
    if (!release.key) return;
    await this.toggleSubscription('release', release.key);
  };

  // Removes any subscription tracked for a saved search that has just been deleted, so a
  // stale bell state does not linger. Reassigns the set to refresh bindings.
  forgetSavedSearch(id: number): void {
    const key = String(id);
    if (!this.savedSearchSubscriptions.has(key)) return;
    const next = new Set(this.savedSearchSubscriptions);
    next.delete(key);
    this.savedSearchSubscriptions = next;
    this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
  }

  // Shared add/remove for a subscription with optimistic UI: flip the tracking set immediately,
  // send the request, and roll back on failure. Guards against double-taps via the pending set.
  // Sets are reassigned (not mutated in place) so Aurelia re-evaluates the bell bindings.
  private async toggleSubscription(kind: 'saved-search' | 'release', targetKey: string): Promise<void> {
    const isReleaseKind = kind === 'release';
    const current = isReleaseKind ? this.releaseSubscriptions : this.savedSearchSubscriptions;
    await this.setSubscription(kind, targetKey, !current.has(targetKey));
  }

  // Applies a desired subscribe/unsubscribe state for a target with optimistic UI: it flips the
  // tracking set immediately, sends the request, and rolls back on failure. A no-op when the target
  // is already in the desired state, when not signed in, or when a request is already in flight.
  private async setSubscription(kind: 'saved-search' | 'release', targetKey: string, subscribe: boolean): Promise<void> {
    if (!this.account.isAuthenticated || this.pendingSubscriptions.has(targetKey)) return;

    const isReleaseKind = kind === 'release';
    const current = isReleaseKind ? this.releaseSubscriptions : this.savedSearchSubscriptions;
    const wasSubscribed = current.has(targetKey);
    if (wasSubscribed === subscribe) return;
    const method = subscribe ? 'POST' : 'DELETE';

    // Optimistic flip so the bell responds instantly.
    this.applySubscription(kind, targetKey, subscribe);
    this.pendingSubscriptions = new Set(this.pendingSubscriptions).add(targetKey);
    this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);

    try {
      const response = await this.http.sendWithCsrf(method, '/api/subscriptions', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, targetKey })
      });
      // DELETE returns 204; a 404 on delete means it was already gone — both are fine.
      const ok = response.ok || (method === 'DELETE' && response.status === 404);
      if (!ok) {
        // A 409 on a release subscribe means the per-user reminder cap was reached; surface it.
        if (subscribe && isReleaseKind && response.status === 409) {
          // The body carries the configured cap so the toast can name it; fall back to a
          // generic message when the payload is missing or unreadable.
          let limit: number | null = null;
          try {
            const body = await response.json();
            if (typeof body?.limit === 'number') limit = body.limit;
          } catch {
            // Ignore: the generic message still explains what happened.
          }
          this.showLimitReached(limit);
        }
        // Roll back the optimistic change.
        this.applySubscription(kind, targetKey, wasSubscribed);
      }
    } catch {
      this.applySubscription(kind, targetKey, wasSubscribed);
    } finally {
      const pending = new Set(this.pendingSubscriptions);
      pending.delete(targetKey);
      this.pendingSubscriptions = pending;
      this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
    }
  }

  // Adds or removes a target key from the relevant subscription set, reassigning the set so the
  // bindings that call isReleaseSubscribed/isSavedSearchSubscribed re-render.
  private applySubscription(kind: 'saved-search' | 'release', targetKey: string, subscribe: boolean): void {
    if (kind === 'release') {
      const next = new Set(this.releaseSubscriptions);
      if (subscribe) next.add(targetKey); else next.delete(targetKey);
      this.releaseSubscriptions = next;
    } else {
      const next = new Set(this.savedSearchSubscriptions);
      if (subscribe) next.add(targetKey); else next.delete(targetKey);
      this.savedSearchSubscriptions = next;
    }
    this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
  }

  // Surfaces the limit-reached message and auto-clears it after a few seconds so the UI
  // does not keep it pinned. Reassigns the timer so rapid taps reset the countdown. The message
  // explains that the reminder was not set and how to free a slot, since the bell silently rolls
  // back otherwise and the user has no other feedback.
  private showLimitReached(limit: number | null = null): void {
    this.limitReachedMessage = limit !== null
      ? `Reminder not set \u2014 you've reached your limit of ${limit} upcoming reminders. ` +
        'Turn one off to add another.'
      : "Reminder not set \u2014 you've reached your limit of upcoming reminders. " +
        'Turn one off to add another.';
    if (this.limitReachedTimer !== null) {
      clearTimeout(this.limitReachedTimer);
    }
    this.limitReachedTimer = setTimeout(() => {
      this.limitReachedMessage = null;
      this.limitReachedTimer = null;
      this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
    }, 6000);
    this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
  }

  // Lets the user dismiss the toast before it auto-clears.
  dismissLimitReached = (): void => {
    if (this.limitReachedTimer !== null) {
      clearTimeout(this.limitReachedTimer);
      this.limitReachedTimer = null;
    }
    this.limitReachedMessage = null;
    this.signaler.dispatchSignal(SUBSCRIPTIONS_SIGNAL);
  };
}
